import React, { useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import SiteLogo from "@/components/SiteLogo";
import { Sparkles } from "lucide-react";

/**
 * Full-screen maintenance page for public visitors (not shown under /admin).
 */
export default function MaintenanceScreen({ settings }) {
  const { lang } = useLanguage();
  const headline = lang === "en" ? settings.headline_en : settings.headline_ro;
  const bodyText = lang === "en" ? settings.body_en : settings.body_ro;

  const [pulse, setPulse] = useState(true);
  useEffect(() => {
    const t = setInterval(() => setPulse((p) => !p), 2800);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const id = setInterval(async () => {
      try {
        const r = await fetch("/api/maintenance-status.php", { cache: "no-store" });
        const j = await r.json();
        if (j.ok && !j.enabled) window.location.reload();
      } catch {
        /* ignore */
      }
    }, 12000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative min-h-screen overflow-hidden bg-[#1a1210] text-[#fbd9e5]">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          background:
            "radial-gradient(ellipse 80% 60% at 50% -10%, rgba(251,217,229,0.35), transparent 55%), radial-gradient(ellipse 60% 40% at 100% 50%, rgba(183,110,121,0.12), transparent 50%), radial-gradient(ellipse 50% 35% at 0% 80%, rgba(251,217,229,0.08), transparent 45%)",
        }}
      />
      <div className="pointer-events-none absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.03\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/svg%3E')]" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center px-6 py-16 text-center">
        <div className="mb-10 inline-flex items-center justify-center rounded-[2rem] border border-white/10 bg-white/[0.06] px-8 py-6 shadow-[0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-md">
          <SiteLogo variant="header" />
        </div>

        <div
          className={`mb-6 inline-flex items-center gap-2 rounded-full border border-[#fbd9e5]/25 bg-[#fbd9e5]/10 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#fbd9e5]/90 transition-opacity duration-1000 ${pulse ? "opacity-100" : "opacity-75"}`}
        >
          <Sparkles className="h-3.5 w-3.5" aria-hidden />
          {lang === "en" ? "Maintenance" : "Mentenanță"}
        </div>

        <h1 className="text-balance text-3xl font-black tracking-tight text-white sm:text-4xl">{headline}</h1>

        <div className="mx-auto mt-8 h-px w-24 bg-gradient-to-r from-transparent via-[#fbd9e5]/50 to-transparent" aria-hidden />

        <p className="mt-8 max-w-md text-pretty text-base leading-relaxed text-[#fbd9e5]/78">{bodyText}</p>

        <div className="mt-12 rounded-2xl border border-white/10 bg-black/20 px-6 py-4 text-sm text-[#fbd9e5]/55 backdrop-blur-sm">
          <p className="font-semibold text-[#fbd9e5]/75">Social Destiny</p>
          <p className="mt-1 text-xs text-[#fbd9e5]/45">{lang === "en" ? "Digital agency · Romania" : "Agenție digitală · România"}</p>
        </div>
      </div>
    </div>
  );
}
